// VisitorFormAutoTime.jsx
import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import { BASE_URL } from "./baseurl";

export default function VisitorFormAutoTime() {
  const HARDCODE_PHONE = "9876543210";
  const HARDCODE_SOCIETY = "Sunrise Residency";
  const HARDCODE_GUARD = "Ramesh Guard";

  const [visitorName, setVisitorName] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");
  const [date, setDate] = useState("");
  const [timeIN, setTimeIN] = useState("");
  const [societyName, setSocietyName] = useState(HARDCODE_SOCIETY);
  const [guardName, setGuardName] = useState(HARDCODE_GUARD);
  const [guardPhoneNumber, setGuardPhoneNumber] = useState(HARDCODE_PHONE);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const scanBufferRef = useRef("");
  const scanLastTsRef = useRef(0);
  const SCAN_MAX_KEY_INTERVAL = 100; // ms
  const MIN_SCAN_LENGTH = 3;

  function nowDate() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  }

  function nowTime() {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }

  function setNow() {
    setDate(nowDate());
    setTimeIN(nowTime());
  }

  function onKeyDown(e) {
    const now = Date.now();
    const lastTs = scanLastTsRef.current || 0;
    const interval = now - lastTs;

    if (interval <= SCAN_MAX_KEY_INTERVAL) {
      if (e.key.length === 1) scanBufferRef.current += e.key;
    } else {
      scanBufferRef.current = e.key.length === 1 ? e.key : "";
    }

    scanLastTsRef.current = now;

    if (e.key === "Enter") {
      const scanned = scanBufferRef.current.trim();
      scanBufferRef.current = "";
      scanLastTsRef.current = 0;

      if (scanned.length >= MIN_SCAN_LENGTH) {
        setVisitorPhone(scanned);
        setNow();
      }
    }
  }

  useEffect(() => {
    // initialize values on mount
    setNow();
    setSocietyName(HARDCODE_SOCIETY);
    setGuardName(HARDCODE_GUARD);
    setGuardPhoneNumber(HARDCODE_PHONE);
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // intentionally empty deps: we want a single listener for the component lifetime
  }, []);

  function validate() {
    if (!visitorName.trim()) return "Visitor name is required";
    if (!visitorPhone.trim()) return "Phone number required";
    if (!timeIN.trim()) return "Time IN required";
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const v = validate();
    if (v) {
      setError(v);
      setTimeout(() => setError(null), 4000);
      return;
    }

    const fd = new FormData();
    fd.append("visitorName", visitorName);
    fd.append("visitorPhone", visitorPhone);
    fd.append("guardPhoneNumber", guardPhoneNumber); // hard-coded
    fd.append("societyName", societyName);
    fd.append("guardName", guardName);
    fd.append("timeIN", timeIN);

    try {
      setLoading(true);

      const res = await fetch(`${BASE_URL}/visitor/add-phone-by-visitor`, {
        method: "POST",
        body: fd,
        // if your backend requires cookies include credentials: 'include'
      });

      const text = await res.text();

      if (!res.ok) {
        // if server returned an error message in body, show it
        throw new Error(text || res.statusText || "Server error");
      }

      // SweetAlert2 - success popup
      await Swal.fire({
        title: "Thank you!",
        html: `<strong>${visitorName || "Visitor"}</strong> added successfully ✅`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      // CLEAR the user-editable fields after success
      setVisitorName("");
      setVisitorPhone("");
      // reset date/time to now so the form shows current timestamp for next entry
      setNow();
    } catch (err) {
      setError(err.message || "Network error");
      setTimeout(() => setError(null), 4000);
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #e6e7eb",
    boxSizing: "border-box",
    fontSize: 16,
  };

  return (
    <div className="v-bg">
      <div className="v-wrap">
        <div className="v-card" role="main">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="field">
                <label>Visitor Name</label>
                <input
                  type="text"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  style={inputStyle}
                  placeholder="Enter visitor name"
                />
              </div>

              <div className="field">
                <label>Guard Phone Number (hard-coded)</label>
                <input type="text" value={guardPhoneNumber} readOnly style={inputStyle} />
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label>Date</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
              </div>

              <div className="field">
                <label>Visitor Phone Number</label>
                <input
                  type="text"
                  value={visitorPhone}
                  onChange={(e) => setVisitorPhone(e.target.value)}
                  style={inputStyle}
                  placeholder="Type phone or scan barcode (press Enter after scan)"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label>Society</label>
                <input type="text" value={societyName} readOnly style={inputStyle} />
              </div>

              <div className="field">
                <label>Time In</label>
                <input type="text" value={timeIN} readOnly style={inputStyle} />
              </div>
            </div>

            <div className="form-row single">
              <div className="field">
                <label>Guard Name</label>
                <input type="text" value={guardName} readOnly style={inputStyle} />
              </div>
            </div>

            <div className="form-row single">
              <button type="submit" disabled={loading} className="submit-btn">
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>

            {error && <div className="error">{error}</div>}
          </form>
        </div>
      </div>

      {/* Responsive styles */}
      <style>{`
        .v-bg {
          min-height: 100vh;
          padding: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg,#0ea5e9,#7c3aed 40%,#60a5fa 80%);
        }

        .v-wrap {
          width: 100%;
          max-width: 980px;
          padding: 18px;
        }

        .v-card {
          background: rgba(255,255,255,0.98);
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
        }

        form {
          display: block;
        }

        .form-row {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 12px;
        }

        .form-row.single {
          align-items: center;
        }

        .field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        label {
          font-size: 14px;
          color: #111827;
        }

        .submit-btn {
          width: 100%;
          padding: 12px 16px;
          border-radius: 10px;
          border: none;
          background: #0f172a;
          color: #fff;
          font-size: 16px;
          cursor: pointer;
        }

        .submit-btn[disabled] {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .error {
          color: #b91c1c;
          margin-top: 10px;
          font-weight: 500;
        }

        /* Larger screens: two-column layout */
        @media (min-width: 720px) {
          .form-row {
            flex-direction: row;
          }
          .form-row.single {
            flex-direction: row;
          }
          .field {
            flex: 1 1 0;
          }
          .form-row .field + .field {
            margin-left: 12px;
          }
          .submit-btn {
            width: auto;
            padding: 10px 18px;
            min-width: 180px;
            align-self: flex-end;
          }
        }

        /* Bigger padding for very large screens */
        @media (min-width: 1100px) {
          .v-wrap {
            padding: 28px;
          }
          .v-card {
            padding: 28px;
          }
        }
      `}</style>
    </div>
  );
}
